import { User } from '../../types';
import { normalizeSettingsTabsFromDb } from '../../utils/permissions';
import { rhUserRowSchema, type RhUserRow } from '../rhDataSchemas';
import { toAppUser } from './mappers';
import { getCompanyId } from './companyService';
import type { DbClient } from './types';

/**
 * Agrupa permissões do usuário com defaults de abas de settings.
 * @param user - Usuário do app
 */
function mergeUserPermissions(user: User): User['permissions'] {
  const p = user.permissions;
  const settingsTabs = p.settingsTabs ?? normalizeSettingsTabsFromDb(p.settings, null);
  return { ...p, settingsTabs };
}

/**
 * Chama endpoint administrativo de usuários na API Postgres.
 * @param sb - Cliente API
 * @param action - create | update | delete
 * @param payload - Dados da operação
 */
async function callAdminUserApi(
  sb: DbClient,
  action: 'create' | 'update' | 'delete',
  payload: { email?: string; password?: string; userId?: string },
): Promise<{ userId?: string; ok?: boolean; error?: string }> {
  try {
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session?.user) {
      throw new Error('Sessão não autenticada');
    }

    const data = await sb.callAdminUserAction(action, payload);
    if (data?.error) {
      throw new Error(data.error);
    }
    return data;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[Admin API] Erro ao chamar manage-user:', message);
    return { error: message };
  }
}

/**
 * Atualiza permissões, username e empresas sem alterar senha.
 * @param sb - Cliente API
 * @param userId - UUID em rh_users
 * @param user - Dados do app (sem senha)
 */
async function syncUserProfile(sb: DbClient, userId: string, user: User): Promise<void> {
  const perms = mergeUserPermissions(user);
  const { error: patchErr } = await sb
    .from('rh_users')
    .update({
      username: user.username,
      perm_dashboard: perms.dashboard,
      perm_hr: perms.hr,
      perm_settings: perms.settings,
      perm_absences: perms.absences,
      perm_employee_create: perms.employeeCreate,
      perm_employee_edit: perms.employeeEdit,
      perm_employee_delete: perms.employeeDelete,
      perm_settings_detail: perms.settings ? perms.settingsTabs : null,
    })
    .eq('id', userId);
  if (patchErr) throw patchErr;

  await sb.from('rh_user_companies').delete().eq('user_id', userId);
  for (const companyName of user.allowedCompanies) {
    const companyId = await getCompanyId(sb, companyName);
    if (companyId) {
      await sb.from('rh_user_companies').upsert(
        { user_id: userId, company_id: companyId },
        { onConflict: 'user_id,company_id' },
      );
    }
  }
}

/** Lista usuários com empresas vinculadas. */
export async function loadUsers(sb: DbClient): Promise<User[]> {
  const { data: rows, error } = await sb.fetchUsersWithCompanies();
  if (error) throw error;

  const grouped = new Map<string, { user: RhUserRow; companies: string[] }>();
  for (const row of rows ?? []) {
    const { company_name, ...userFields } = row;
    const parsed = rhUserRowSchema.safeParse(userFields);
    if (!parsed.success) {
      console.warn('[rhDataService] Linha inválida em rh_users:', parsed.error.flatten().fieldErrors);
      continue;
    }
    const userId = parsed.data.id;
    if (!grouped.has(userId)) {
      grouped.set(userId, { user: parsed.data, companies: [] });
    }
    if (company_name) {
      grouped.get(userId)!.companies.push(String(company_name));
    }
  }

  return Array.from(grouped.values()).map(({ user, companies }) => toAppUser(user, companies));
}

/** @deprecated Preferir saveUserWithAuth — senha só no servidor. */
export async function saveUser(sb: DbClient, user: User): Promise<string> {
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(user.id);
  const perms = mergeUserPermissions(user);
  const payload: Record<string, unknown> = {
    username: user.username,
    perm_dashboard: perms.dashboard,
    perm_hr: perms.hr,
    perm_settings: perms.settings,
    perm_absences: perms.absences,
    perm_employee_create: perms.employeeCreate,
    perm_employee_edit: perms.employeeEdit,
    perm_employee_delete: perms.employeeDelete,
    perm_settings_detail: perms.settings ? perms.settingsTabs : null,
  };

  let userId: string;
  if (isUuid) {
    const { data, error } = await sb
      .from('rh_users')
      .upsert({ id: user.id, ...payload }, { onConflict: 'id' })
      .select('id')
      .single();
    if (error) throw error;
    userId = data.id;
  } else {
    const { data, error } = await sb
      .from('rh_users')
      .upsert(payload, { onConflict: 'username' })
      .select('id')
      .single();
    if (error) throw error;
    userId = data.id;
  }

  await sb.from('rh_user_companies').delete().eq('user_id', userId);
  for (const companyName of user.allowedCompanies) {
    const companyId = await getCompanyId(sb, companyName);
    if (companyId) {
      await sb.from('rh_user_companies').upsert(
        { user_id: userId, company_id: companyId },
        { onConflict: 'user_id,company_id' },
      );
    }
  }
  return userId;
}

/** Remove usuário de rh_users. */
export async function deleteUser(sb: DbClient, userId: string): Promise<void> {
  const { error } = await sb.from('rh_users').delete().eq('id', userId);
  if (error) throw error;
}

/**
 * Cria ou atualiza usuário via Admin API + sincroniza rh_users.
 * @param plainPassword - Senha em texto; omitida em update mantém a atual
 */
export async function saveUserWithAuth(
  sb: DbClient,
  user: User,
  plainPassword?: string,
): Promise<string> {
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(user.id);
  const fallbackPassword = 'TroqueEssaSenha@123';

  let authUserId: string;

  if (isUuid) {
    const result = await callAdminUserApi(sb, 'update', {
      userId: user.id,
      email: user.username,
      password: plainPassword,
    });
    if (result.error) throw new Error(result.error);
    authUserId = result.userId!;
  } else {
    const result = await callAdminUserApi(sb, 'create', {
      email: user.username,
      password: plainPassword ?? fallbackPassword,
    });
    if (result.error) throw new Error(result.error);
    authUserId = result.userId!;
  }

  await syncUserProfile(sb, authUserId, user);
  return authUserId;
}

/** Remove usuário via Admin API e rh_users. */
export async function deleteUserWithAuth(sb: DbClient, userId: string): Promise<void> {
  const result = await callAdminUserApi(sb, 'delete', { userId });
  if (result.error && !result.error.toLowerCase().includes('not found')) {
    throw new Error(result.error);
  }
  const { error } = await sb.from('rh_users').delete().eq('id', userId);
  if (error) throw error;
}
